import * as React from "react";
import { analytics } from "@shared/utils/analytics";
import { Spinner } from "@boundary/components/spinner";
import styles from "./slideToConfirm.module.scss";

// Доля ширины дорожки, после которой свайп засчитывается.
const CONFIRM_THRESHOLD = 0.9;
const KNOB_WIDTH_PX = 48;
const KNOB_INSET_PX = 4;

export interface SlideToConfirmProps {
  label: string;
  hint?: string;
  /** Причина неудачи последней попытки; показывается вместо подсказки */
  error?: string;
  disabled?: boolean;
  loading?: boolean;
  analyticsName?: string;
  onConfirm: () => void;
  className?: string;
}

function ArrowIcon() {
  return (
    <svg
      width={20}
      height={16}
      viewBox="0 0 20 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={styles.arrow}
      aria-hidden="true"
    >
      <path
        d="M19.7071 8.70711C20.0976 8.31658 20.0976 7.68342 19.7071 7.29289L13.3431 0.928932C12.9526 0.538408 12.3195 0.538408 11.9289 0.928932C11.5384 1.31946 11.5384 1.95262 11.9289 2.34315L17.5858 8L11.9289 13.6569C11.5384 14.0474 11.5384 14.6805 11.9289 15.0711C12.3195 15.4616 12.9526 15.4616 13.3431 15.0711L19.7071 8.70711ZM0 8V9H19V8V7H0V8Z"
        fill="currentColor"
      />
    </svg>
  );
}

/**
 * Подтверждение действия свайпом. Нужно провести ползунок до конца дорожки —
 * защита от случайного нажатия для необратимых действий.
 */
export function SlideToConfirm({
  label,
  hint,
  error,
  disabled = false,
  loading = false,
  analyticsName,
  onConfirm,
  className,
}: SlideToConfirmProps) {
  const trackRef = React.useRef<HTMLDivElement>(null);
  const [offset, setOffset] = React.useState(0);
  const [dragging, setDragging] = React.useState(false);
  const startXRef = React.useRef(0);
  const maxOffsetRef = React.useRef(0);

  const locked = disabled || loading;

  const maxOffset = React.useCallback(() => {
    const width = trackRef.current?.offsetWidth ?? 0;
    return Math.max(0, width - KNOB_WIDTH_PX - KNOB_INSET_PX * 2);
  }, []);

  const handlePointerDown = React.useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (locked) return;
      startXRef.current = e.clientX;
      maxOffsetRef.current = maxOffset();
      setDragging(true);
      e.currentTarget.setPointerCapture(e.pointerId);
    },
    [locked, maxOffset]
  );

  const handlePointerMove = React.useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!dragging) return;
      const delta = e.clientX - startXRef.current;
      setOffset(Math.min(Math.max(0, delta), maxOffsetRef.current));
    },
    [dragging]
  );

  const handlePointerUp = React.useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!dragging) return;
      setDragging(false);
      e.currentTarget.releasePointerCapture(e.pointerId);

      const limit = maxOffsetRef.current;
      const confirmed = limit > 0 && offset >= limit * CONFIRM_THRESHOLD;

      // Ползунок всегда возвращается в начало: состояние действия показывает
      // не он, а сама страница (спиннер, смена статуса заказа).
      setOffset(0);

      if (confirmed) {
        analytics.logButtonClick(analyticsName || label, { control: "slide" });
        onConfirm();
      }
    },
    [dragging, offset, analyticsName, label, onConfirm]
  );

  const progress = maxOffsetRef.current > 0 ? offset / maxOffsetRef.current : 0;

  const trackClasses = [styles.track, dragging ? styles.trackDragging : "", className]
    .filter(Boolean)
    .join(" ");
  const knobClasses = [styles.knob, dragging ? "" : styles.knobIdle].filter(Boolean).join(" ");

  return (
    <div className={styles.wrapper}>
      <div
        ref={trackRef}
        className={trackClasses}
        role="button"
        tabIndex={locked ? -1 : 0}
        aria-disabled={locked}
        aria-label={label}
        data-disabled={locked ? "true" : undefined}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
      >
        <div className={styles.fill} style={{ transform: `scaleX(${progress})` }} />
        <span className={styles.label}>{label}</span>
        <div className={knobClasses} style={{ transform: `translateX(${offset}px)` }}>
          {loading ? <Spinner size={28} /> : <ArrowIcon />}
        </div>
      </div>
      {error || hint ? (
        <p className={error ? `${styles.hint} ${styles.hintError}` : styles.hint}>
          {error || hint}
        </p>
      ) : null}
    </div>
  );
}
